import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ChevronLeft, Loader2, Check, Search, Wrench } from "lucide-react";

/**
 * Super Admin — Classification Backfill Tool.
 *
 * Lists every person whose `classification` is exactly 25 characters — the
 * historical `[:25]` truncation cap silently damaged this many records.
 * Each row shows name, phone and the current (truncated) value; admins edit
 * and save the corrected full text inline (server hard-caps at 70 chars).
 */
export default function ClassificationBackfill() {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [drafts, setDrafts] = useState({});          // id -> current text
    const [saving, setSaving] = useState({});          // id -> boolean
    const [savedAt, setSavedAt] = useState({});        // id -> timestamp
    const [q, setQ] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/super/classification-backfill/candidates");
            const rows = data?.items || [];
            setItems(rows);
            setDrafts(Object.fromEntries(rows.map((r) => [r.id, r.classification || ""])));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load candidates");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return items;
        return items.filter((r) =>
            (r.name || "").toLowerCase().includes(needle) ||
            (r.phone || "").includes(needle) ||
            (r.classification || "").toLowerCase().includes(needle),
        );
    }, [items, q]);

    const save = async (row) => {
        const id = row.id;
        const value = (drafts[id] || "").trim();
        if (value === (row.classification || "").trim()) {
            toast.info("No change to save");
            return;
        }
        setSaving((s) => ({ ...s, [id]: true }));
        try {
            const { data } = await api.patch(`/super/persons/${id}/classification`, {
                classification: value,
            });
            const newLen = data?.length ?? value.length;
            toast.success(`Saved ${row.name} — ${newLen}/70 chars`);
            setSavedAt((s) => ({ ...s, [id]: Date.now() }));
            // If the new length is no longer exactly 25, drop the row from the
            // remaining list so admins see progress. If they saved with exactly
            // 25 chars again (unlikely), keep it.
            setItems((prev) => (newLen === 25 ? prev : prev.filter((r) => r.id !== id)));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving((s) => ({ ...s, [id]: false }));
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-rotary-cream via-white to-rotary-cream/40">
            <header className="max-w-6xl mx-auto px-6 pt-8 pb-4">
                <Link to="/admin/platform" className="inline-flex items-center gap-1 text-sm text-rotary-blue hover:underline" data-testid="cbackfill-back-link">
                    <ChevronLeft className="h-4 w-4" /> Back to platform
                </Link>
                <h1 className="rotary-heading text-3xl sm:text-4xl mt-3 flex items-center gap-2">
                    <Wrench className="h-6 w-6 text-rotary-blue" /> Classification Backfill
                </h1>
                <p className="text-sm text-muted-foreground max-w-2xl mt-2">
                    These records were silently truncated to exactly 25 characters by an earlier build. Enter the correct full text (up to 70 characters) and Save to restore each one. Rows disappear from the list as you fix them.
                </p>
            </header>

            <main className="max-w-6xl mx-auto px-6 pb-16">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name, phone or current text…"
                            className="pl-9 rounded-full"
                            data-testid="cbackfill-search-input"
                        />
                    </div>
                    <Button
                        variant="outline"
                        onClick={load}
                        className="rounded-full"
                        data-testid="cbackfill-refresh-btn"
                    >
                        {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                        Refresh
                    </Button>
                    <span className="ml-auto text-xs text-muted-foreground" data-testid="cbackfill-count">
                        {loading ? "Loading…" : `${filtered.length.toLocaleString()} of ${items.length.toLocaleString()} record${items.length === 1 ? "" : "s"}`}
                    </span>
                </div>

                {loading ? (
                    <Card className="p-10 text-center text-sm text-muted-foreground">
                        <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" />
                        Loading truncated records…
                    </Card>
                ) : filtered.length === 0 ? (
                    <Card className="p-10 text-center" data-testid="cbackfill-empty">
                        <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-green-100 grid place-items-center">
                            <Check className="h-5 w-5 text-green-600" />
                        </div>
                        <p className="text-sm">
                            {items.length === 0
                                ? "Nothing to backfill — every classification is either empty or longer than 25 characters."
                                : "No records match your search."}
                        </p>
                    </Card>
                ) : (
                    <Card className="overflow-hidden">
                        <div className="grid grid-cols-[minmax(160px,1.2fr)_minmax(120px,0.7fr)_minmax(220px,2fr)_auto] gap-3 px-4 py-3 border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <div>Name</div>
                            <div>Phone</div>
                            <div>Corrected classification</div>
                            <div className="text-right">Action</div>
                        </div>
                        <ul className="divide-y" data-testid="cbackfill-list">
                            {filtered.map((row) => {
                                const draft = drafts[row.id] ?? "";
                                const isSaving = !!saving[row.id];
                                const justSaved = !!savedAt[row.id];
                                const dirty = draft.trim() !== (row.classification || "").trim();
                                return (
                                    <li
                                        key={row.id}
                                        className="grid grid-cols-[minmax(160px,1.2fr)_minmax(120px,0.7fr)_minmax(220px,2fr)_auto] gap-3 items-start px-4 py-3"
                                        data-testid={`cbackfill-row-${row.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate" title={row.name}>{row.name}</div>
                                            <div className="text-[11px] text-muted-foreground">was: {row.classification}</div>
                                        </div>
                                        <div className="text-sm tabular-nums">{row.phone}</div>
                                        <div className="min-w-0">
                                            <Input
                                                value={draft}
                                                onChange={(e) => setDrafts((s) => ({ ...s, [row.id]: e.target.value.slice(0, 70) }))}
                                                maxLength={70}
                                                placeholder="Enter the full classification…"
                                                data-testid={`cbackfill-input-${row.id}`}
                                            />
                                            <div className="flex items-center justify-between mt-1 text-[11px]">
                                                <span className={dirty ? "text-rotary-blue" : "text-muted-foreground"} data-testid={`cbackfill-status-${row.id}`}>
                                                    {justSaved ? "Saved ✓" : (dirty ? "Unsaved changes" : "No change")}
                                                </span>
                                                <span className="tabular-nums text-muted-foreground" data-testid={`cbackfill-counter-${row.id}`}>
                                                    {draft.length}/70
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <Button
                                                size="sm"
                                                onClick={() => save(row)}
                                                disabled={isSaving || !dirty || !draft.trim()}
                                                className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                                                data-testid={`cbackfill-save-${row.id}`}
                                            >
                                                {isSaving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                                                Save
                                            </Button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </Card>
                )}
            </main>
        </div>
    );
}
